import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface UseTabParamOptions {
  /** Query-string key, `tab` by default. */
  param?: string;
  /**
   * Whether a tab may be restored from the URL. Tabs that only make sense after
   * an in-page selection (e.g. a picked supplier) should return false so a
   * refresh lands on the first tab instead of an empty one.
   */
  persist?: (index: number) => boolean;
}

/**
 * Tab state that survives a refresh or a shared link via `?tab=<name>`.
 * Local state stays the source of truth, so `setTab` is still synchronous;
 * the URL is only mirrored (with replace, so tabs don't pile up in history).
 *
 * `panelRef` is optional: attach it to the element that holds the tab content
 * and the content fades in on every switch, without remounting anything.
 */
export function useTabParam(
  names: string[],
  { param = "tab", persist }: UseTabParamOptions = {}
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const canPersist = useRef(persist);
  canPersist.current = persist;
  const namesRef = useRef(names);
  namesRef.current = names;

  const [tab, setTabState] = useState(() => {
    const index = names.indexOf(searchParams.get(param) ?? "");
    return index > 0 && (persist ? persist(index) : true) ? index : 0;
  });

  const setTab = useCallback(
    (index: number) => {
      setTabState(index);
      const name = namesRef.current[index];
      const keep =
        index > 0 &&
        !!name &&
        (canPersist.current ? canPersist.current(index) : true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (keep) next.set(param, name);
          else next.delete(param);
          return next;
        },
        { replace: true }
      );
    },
    [param, setSearchParams]
  );

  const panelRef = useRef<HTMLDivElement | null>(null);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const el = panelRef.current;
    if (!el || typeof el.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const animation = el.animate(
      [
        { opacity: 0, transform: "translateY(4px)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }
    );
    return () => animation.cancel();
  }, [tab]);

  return { tab, setTab, panelRef };
}
